import { useMemo } from 'react'

import { useTheme } from '@mui/material/styles'
import { ChartData, ChartOptions } from 'chart.js'
import { Chart } from 'react-chartjs-2'

import { BID_SPREAD_ANALYSIS_CHART_HEIGHT } from 'components/reports/Metrics/constants/BidSpreadAnalysis'

import { BAR_STYLE } from '../components/chartConfig'
import { BidSpreadAnalysisBucket } from '../components/types'

interface Props {
  buckets: BidSpreadAnalysisBucket[]
  xAxisLabel: string
  height?: number
}

export const BidSpreadAnalysisChart = ({
  buckets,
  xAxisLabel,
  height = BID_SPREAD_ANALYSIS_CHART_HEIGHT,
}: Props) => {
  const theme = useTheme()
  const barColor = theme.palette.primary.main

  const labels = useMemo(() => buckets.map((bucket) => bucket.label), [buckets])

  const data = useMemo<ChartData<'bar', number[], string>>(
    () => ({
      labels,
      datasets: [
        {
          ...BAR_STYLE,
          type: 'bar' as const,
          label: 'Jobs',
          data: buckets.map((bucket) => bucket.jobsCount),
          backgroundColor: barColor,
        },
      ],
    }),
    [labels, buckets, barColor],
  )

  const options = useMemo<ChartOptions<'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: { display: false },
          title: { display: true, text: xAxisLabel },
          ticks: { maxRotation: 0, autoSkip: false },
        },
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Number of jobs' },
          ticks: { precision: 0 },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (context) =>
              `${context.dataset.label}: ${(context.parsed.y ?? 0).toLocaleString()}`,
          },
        },
      },
    }),
    [xAxisLabel],
  )

  return (
    <div style={{ height, position: 'relative' }}>
      <Chart<'bar', number[], string> type='bar' data={data} options={options} />
    </div>
  )
}
